import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2, Download, FileUp, Globe, Mail, MapPin, Phone, Plus, Search, Sparkles, Star, Tag, Trash2, Upload, UserPlus, Users, Cake, PenSquare,
} from 'lucide-react';
import {
  contactsToCsv, contactsToVCard, displayName, domainOf, formatMailbox, isFreemail, parseContactsFile, parseMailbox,
  type Contact, type ContactInput, type ImportedContact,
} from '@dts/core';
import { api, errorMessage } from '@/lib/api';
import { qk, useContacts, useMyAddresses } from '@/lib/queries';
import { composeNew } from '@/lib/compose';
import { listTime } from '@/lib/format';
import { Avatar, Favicon } from '@/components/Avatar';
import { confirmDialog } from '@/components/Dialogs';
import { Button, Chip, cn, Empty, IconButton, Input, Modal, Num, Textarea } from '@/components/ui';

export function Contacts() {
  const q = useContacts();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [tag, setTag] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [editing, setEditing] = useState<ContactInput | Contact | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const contacts = q.data?.contacts ?? [];
  const tags = useMemo(() => Array.from(new Set(contacts.flatMap((c) => c.tags))).sort(), [contacts]);
  const list = useMemo(() => {
    const s = search.toLowerCase();
    return contacts.filter((c) => (!tag || c.tags.includes(tag)) && (!s || [c.name, c.company, ...c.emails, ...c.phones].some((v) => v?.toLowerCase().includes(s))));
  }, [contacts, search, tag]);
  const current = contacts.find((c) => c.id === selected) ?? null;

  useEffect(() => {
    if (!selected && list[0]) setSelected(list[0].id);
  }, [list, selected]);

  const exportAs = async (kind: 'vcf' | 'csv') => {
    const path = await window.dts.files.saveText(`contacts.${kind}`, kind === 'vcf' ? contactsToVCard(contacts) : contactsToCsv(contacts));
    if (path) toast.success(`Exported ${contacts.length} contacts`);
  };

  return (
    <div className="flex h-full min-h-0">
      <div className="flex w-[380px] shrink-0 flex-col border-r border-line">
        <div className="px-5 pb-3 pt-1">
          <div className="flex items-end justify-between">
            <div>
              <h1 className="page-title text-[22px] font-semibold tracking-tight">Contacts</h1>
              <div className="text-[12px] text-muted">{q.data?.total ?? 0} people</div>
            </div>
            <div className="flex gap-1">
              <IconButton label="Import CSV / vCard" onClick={() => setImportOpen(true)}><Upload /></IconButton>
              <IconButton label="Export vCard" onClick={() => void exportAs('vcf')}><Download /></IconButton>
              <IconButton label="New contact" onClick={() => setEditing({ name: '', emails: [], phones: [], tags: [], favorite: false })}><Plus /></IconButton>
            </div>
          </div>
          <div className="relative mt-3">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search people" className="pl-9" />
          </div>
          {tags.length > 0 && (
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              <Chip active={!tag} onClick={() => setTag(null)}>All</Chip>
              {tags.map((t) => <Chip key={t} active={tag === t} onClick={() => setTag(tag === t ? null : t)}>{t}</Chip>)}
            </div>
          )}
        </div>
        <SuggestedContacts known={contacts} />
        <div className="min-h-0 flex-1 overflow-y-auto px-2.5 pb-4">
          {q.isLoading ? null : list.length === 0 ? (
            <Empty icon={<Users />} title="No contacts yet" hint="Import from Google, Outlook or iCloud (CSV / vCard), or add people from emails.">
              <Button variant="primary" icon={<Upload className="size-4" />} onClick={() => setImportOpen(true)}>Import contacts</Button>
            </Empty>
          ) : (
            list.map((c, i) => (
              <motion.button
                key={c.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: Math.min(i, 15) * 0.015 }}
                onClick={() => setSelected(c.id)}
                className={cn('flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition', selected === c.id ? 'bg-accent/[0.13]' : 'hover:bg-line')}
              >
                <Avatar name={c.name} address={c.emails[0] ?? ''} size={34} />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5 truncate text-[13.5px] font-medium">
                    {c.name}
                    {c.favorite && <Star className="size-3 fill-warn text-warn" />}
                  </span>
                  <span className="block truncate text-[12px] text-muted">{c.company ? `${c.title ? `${c.title}, ` : ''}${c.company}` : c.emails[0]}</span>
                </span>
              </motion.button>
            ))
          )}
        </div>
      </div>

      <div className="min-w-0 flex-1 overflow-y-auto">
        <AnimatePresence mode="wait">
          {current ? (
            <ContactDetail key={current.id} contact={current} onEdit={() => setEditing(current)} onDeleted={() => setSelected(null)} />
          ) : (
            <Empty icon={<Users />} title="Pick a contact" />
          )}
        </AnimatePresence>
      </div>

      <ContactEditor value={editing} onClose={() => setEditing(null)} onSaved={(c) => (setSelected(c.id), qc.invalidateQueries({ queryKey: qk.contacts }))} />
      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} />
    </div>
  );
}

function ContactDetail({ contact: c, onEdit, onDeleted }: { contact: Contact; onEdit: () => void; onDeleted: () => void }) {
  const qc = useQueryClient();
  const email = c.emails[0];
  const company = c.website ? c.website.replace(/^https?:\/\//, '').split('/')[0].replace(/^www\./, '') : email && !isFreemail(domainOf(email)) ? domainOf(email) : null;
  const history = useQuery({
    queryKey: ['contact-mail', email],
    enabled: !!email,
    queryFn: () => api.listMessages({ search: email, top: 30 }),
    staleTime: 60_000,
  });
  const [enriching, setEnriching] = useState(false);

  const toggleFav = async () => {
    await api.updateContact({ ...c, favorite: !c.favorite });
    qc.invalidateQueries({ queryKey: qk.contacts });
  };
  const remove = async () => {
    if (!(await confirmDialog(`Delete ${c.name}?`, undefined, { confirmLabel: 'Delete', danger: true }))) return;
    await api.deleteContact(c.id);
    qc.invalidateQueries({ queryKey: qk.contacts });
    onDeleted();
  };
  const enrich = async () => {
    const latest = history.data?.items.find((m) => m.fromAddress === email);
    if (!latest) {
      toast.message('No email from this contact to learn from yet.');
      return;
    }
    setEnriching(true);
    try {
      const x = await api.extractContact(latest.id);
      const next: Contact = {
        ...c,
        company: c.company || x.company || undefined,
        title: c.title || x.title || undefined,
        website: c.website || x.website || undefined,
        address: c.address || x.address || undefined,
        phones: Array.from(new Set([...c.phones, ...(x.phones ?? [])])),
        emails: Array.from(new Set([...c.emails, ...(x.emails ?? []).map((e) => e.toLowerCase())])),
      };
      await api.updateContact(next);
      qc.invalidateQueries({ queryKey: qk.contacts });
      toast.success('Contact enriched from their email signature');
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setEnriching(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mx-auto max-w-[760px] px-10 py-8">
      <div className="flex items-start gap-5">
        <Avatar name={c.name} address={email ?? ''} size={84} />
        <div className="min-w-0 flex-1 pt-1">
          <h2 className="selectable text-[28px] font-semibold tracking-tight">{c.name}</h2>
          {(c.title || c.company) && (
            <div className="mt-0.5 flex items-center gap-2 text-[14px] text-muted">
              {company && <Favicon domain={company} size={16} />}
              {[c.title, c.company].filter(Boolean).join(' · ')}
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            {email && <Button variant="primary" icon={<PenSquare className="size-4" />} onClick={() => composeNew({ to: [formatMailbox({ name: c.name, address: email })] })}>Email</Button>}
            <Button variant="soft" icon={<Sparkles className="size-4 text-accent" />} loading={enriching} onClick={enrich}>Enrich with AI</Button>
            <IconButton label={c.favorite ? 'Unfavorite' : 'Favorite'} onClick={toggleFav} active={c.favorite}><Star className={cn(c.favorite && 'fill-current')} /></IconButton>
            <Button variant="ghost" onClick={onEdit}>Edit</Button>
            <IconButton label="Delete" onClick={remove}><Trash2 /></IconButton>
          </div>
        </div>
      </div>

      <div className="glass mt-8 divide-y divide-line rounded-2xl">
        {c.emails.map((e) => <Field key={e} icon={<Mail />} label="Email" value={e} action={() => composeNew({ to: [formatMailbox({ name: c.name, address: e })] })} />)}
        {c.phones.map((p) => <Field key={p} icon={<Phone />} label="Phone" value={p} />)}
        {c.company && <Field icon={<Building2 />} label="Company" value={c.company} />}
        {c.website && <Field icon={<Globe />} label="Website" value={c.website} action={() => window.dts.app.openExternal(c.website!.startsWith('http') ? c.website! : `https://${c.website}`)} />}
        {c.address && <Field icon={<MapPin />} label="Address" value={c.address} />}
        {c.birthday && <Field icon={<Cake />} label="Birthday" value={c.birthday} />}
        {c.tags.length > 0 && <Field icon={<Tag />} label="Tags" value={c.tags.join(', ')} />}
      </div>
      {c.notes && <div className="glass selectable mt-4 whitespace-pre-wrap rounded-2xl p-4 text-[13.5px] text-fg/85">{c.notes}</div>}

      {email && (
        <div className="mt-8">
          <h3 className="mb-3 text-[14px] font-semibold">Conversations</h3>
          <div className="glass divide-y divide-line overflow-hidden rounded-2xl">
            {history.isLoading && <div className="p-4"><div className="skeleton h-3 w-2/3" /></div>}
            {history.data?.items.length === 0 && <div className="p-5 text-center text-[13px] text-muted">No emails with {c.name} yet.</div>}
            {history.data?.items.slice(0, 12).map((m) => (
              <div key={m.id} className="flex items-center gap-3 px-4 py-2.5">
                <span className={cn('size-1.5 shrink-0 rounded-full', m.isRead ? 'bg-transparent' : 'bg-accent')} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-medium">{m.subject || '(no subject)'}</span>
                  <span className="block truncate text-[12px] text-muted">{m.aiGist ?? m.snippet}</span>
                </span>
                <span className="shrink-0 text-[11.5px] text-faint"><Num>{listTime(m.receivedUtc)}</Num></span>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}

function Field({ icon, label, value, action }: { icon: React.ReactNode; label: string; value: string; action?: () => void }) {
  return (
    <div className="flex items-center gap-4 px-4 py-3">
      <span className="text-muted [&_svg]:size-4">{icon}</span>
      <span className="w-20 text-[12px] text-faint">{label}</span>
      {action ? (
        <button onClick={action} className="selectable flex-1 truncate text-left text-[13.5px] text-accent hover:underline">{value}</button>
      ) : (
        <span className="selectable flex-1 truncate text-[13.5px]">{value}</span>
      )}
    </div>
  );
}

function ContactEditor({ value, onClose, onSaved }: { value: ContactInput | Contact | null; onClose: () => void; onSaved: (c: Contact) => void }) {
  const [form, setForm] = useState<ContactInput | Contact | null>(value);
  const [busy, setBusy] = useState(false);
  useEffect(() => setForm(value), [value]);
  if (!form) return <Modal open={false} onClose={onClose}>{null}</Modal>;
  const set = (patch: Partial<Contact>) => setForm({ ...form, ...patch });
  const lines = (v: string) => v.split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean);
  const save = async () => {
    setBusy(true);
    try {
      const saved = 'id' in form && form.id ? await api.updateContact(form as Contact) : await api.createContact(form);
      onSaved(saved);
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open={!!value} onClose={onClose} title={'id' in form && form.id ? 'Edit contact' : 'New contact'} width={560}>
      <div className="grid grid-cols-2 gap-3 p-5">
        <label className="col-span-2 text-[12px] text-muted">Name<Input value={form.name} onChange={(e) => set({ name: e.target.value })} autoFocus /></label>
        <label className="text-[12px] text-muted">Company<Input value={form.company ?? ''} onChange={(e) => set({ company: e.target.value })} /></label>
        <label className="text-[12px] text-muted">Title<Input value={form.title ?? ''} onChange={(e) => set({ title: e.target.value })} /></label>
        <label className="text-[12px] text-muted">Emails (one per line)<Textarea rows={2} value={form.emails.join('\n')} onChange={(e) => set({ emails: lines(e.target.value) })} /></label>
        <label className="text-[12px] text-muted">Phones (one per line)<Textarea rows={2} value={form.phones.join('\n')} onChange={(e) => set({ phones: lines(e.target.value) })} /></label>
        <label className="text-[12px] text-muted">Website<Input value={form.website ?? ''} onChange={(e) => set({ website: e.target.value })} /></label>
        <label className="text-[12px] text-muted">Birthday<Input value={form.birthday ?? ''} onChange={(e) => set({ birthday: e.target.value })} placeholder="YYYY-MM-DD" /></label>
        <label className="col-span-2 text-[12px] text-muted">Address<Input value={form.address ?? ''} onChange={(e) => set({ address: e.target.value })} /></label>
        <label className="col-span-2 text-[12px] text-muted">Tags (comma separated)<Input value={form.tags.join(', ')} onChange={(e) => set({ tags: lines(e.target.value) })} /></label>
        <label className="col-span-2 text-[12px] text-muted">Notes<Textarea rows={3} value={form.notes ?? ''} onChange={(e) => set({ notes: e.target.value })} /></label>
      </div>
      <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={busy} onClick={save} disabled={!form.name.trim() && form.emails.length === 0}>Save</Button>
      </div>
    </Modal>
  );
}

function ImportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [parsed, setParsed] = useState<{ name: string; contacts: ImportedContact[] } | null>(null);
  const [mode, setMode] = useState<'merge' | 'skip'>('merge');
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const read = async (f: File) => {
    const text = await f.text();
    const contacts = parseContactsFile(f.name, text);
    if (!contacts.length) toast.error('No contacts found in that file.');
    setParsed({ name: f.name, contacts });
  };
  const run = async () => {
    if (!parsed) return;
    setBusy(true);
    try {
      let created = 0, merged = 0, skipped = 0;
      for (let i = 0; i < parsed.contacts.length; i += 1000) {
        const r = await api.importContacts(parsed.contacts.slice(i, i + 1000).map((c) => ({ ...c, source: 'import' })), mode);
        created += r.created;
        merged += r.merged;
        skipped += r.skipped;
      }
      toast.success(`Imported: ${created} new, ${merged} merged${skipped ? `, ${skipped} skipped` : ''}`);
      qc.invalidateQueries({ queryKey: qk.contacts });
      setParsed(null);
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={() => (setParsed(null), onClose())} title="Import contacts" width={680}>
      <div className="p-5">
        {!parsed ? (
          <button
            onClick={() => input.current?.click()}
            onDragOver={(e) => (e.preventDefault(), setOver(true))}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setOver(false);
              const f = e.dataTransfer.files[0];
              if (f) void read(f);
            }}
            className={cn('flex w-full flex-col items-center gap-3 rounded-2xl border-2 border-dashed px-6 py-12 transition', over ? 'border-accent bg-accent/10' : 'border-line-strong hover:border-accent/50')}
          >
            <FileUp className="size-8 text-accent" />
            <div className="text-[14px] font-semibold">Drop a CSV or vCard file</div>
            <div className="max-w-sm text-center text-[12.5px] text-muted">Google Contacts, Outlook, Apple / iCloud, HubSpot or any spreadsheet. Columns are detected automatically; duplicates are merged by email.</div>
            <input ref={input} type="file" accept=".csv,.vcf,.vcard,.txt" hidden onChange={(e) => e.target.files?.[0] && void read(e.target.files[0])} />
          </button>
        ) : (
          <>
            <div className="mb-3 flex items-center justify-between">
              <div className="text-[13.5px]"><b>{parsed.contacts.length}</b> contacts found in <span className="text-muted">{parsed.name}</span></div>
              <button className="text-[12px] text-muted hover:text-fg" onClick={() => setParsed(null)}>Choose another file</button>
            </div>
            <div className="max-h-[320px] overflow-auto rounded-xl border border-line">
              <table className="w-full text-left text-[12.5px]">
                <thead className="sticky top-0 bg-raised text-[11px] uppercase tracking-wider text-faint">
                  <tr><th className="px-3 py-2">Name</th><th className="px-3 py-2">Email</th><th className="px-3 py-2">Company</th><th className="px-3 py-2">Phone</th></tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {parsed.contacts.slice(0, 100).map((c, i) => (
                    <tr key={i}>
                      <td className="px-3 py-1.5 font-medium">{c.name}</td>
                      <td className="px-3 py-1.5 text-muted">{c.emails.join(', ')}</td>
                      <td className="px-3 py-1.5 text-muted">{c.company}</td>
                      <td className="px-3 py-1.5 text-muted">{c.phones?.[0]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {parsed.contacts.length > 100 && <div className="mt-1.5 text-[11.5px] text-faint">Showing the first 100.</div>}
            <div className="mt-4 flex items-center gap-4 text-[13px]">
              <span className="text-muted">Existing contacts with the same email:</span>
              <label className="flex items-center gap-1.5"><input type="radio" checked={mode === 'merge'} onChange={() => setMode('merge')} /> Merge</label>
              <label className="flex items-center gap-1.5"><input type="radio" checked={mode === 'skip'} onChange={() => setMode('skip')} /> Skip</label>
            </div>
          </>
        )}
      </div>
      {parsed && (
        <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
          <Button variant="ghost" onClick={() => setParsed(null)}>Cancel</Button>
          <Button variant="primary" loading={busy} onClick={run}>Import {parsed.contacts.length}</Button>
        </div>
      )}
    </Modal>
  );
}

/** People you email who aren't contacts yet, one click to add. */
function SuggestedContacts({ known }: { known: Contact[] }) {
  const qc = useQueryClient();
  const mine = useMyAddresses();
  const recent = useQuery({ queryKey: ['recent-senders'], queryFn: () => api.listMessages({ top: 150 }), staleTime: 5 * 60_000 });
  const [dismissed, setDismissed] = useState<string[]>([]);
  const suggestions = useMemo(() => {
    const have = new Set(known.flatMap((c) => c.emails));
    const counts = new Map<string, { name: string; address: string; n: number }>();
    for (const m of recent.data?.items ?? []) {
      if (m.folder === 'Junk' || m.folder === 'Trash' || m.aiCategory === 'newsletter' || m.aiCategory === 'promotion' || m.aiCategory === 'notification') continue;
      const from = parseMailbox(m.from) ?? { address: m.fromAddress };
      if (!from.address || have.has(from.address) || mine.includes(from.address) || /no-?reply|notifications?|mailer|bounce/i.test(from.address)) continue;
      const cur = counts.get(from.address) ?? { name: displayName(from), address: from.address, n: 0 };
      cur.n++;
      counts.set(from.address, cur);
    }
    return [...counts.values()].filter((s) => !dismissed.includes(s.address)).sort((a, b) => b.n - a.n).slice(0, 4);
  }, [recent.data, known, mine, dismissed]);

  if (!suggestions.length) return null;
  return (
    <div className="mx-3 mb-3 rounded-2xl border border-line bg-bg/30 p-2.5">
      <div className="mb-1.5 flex items-center gap-1.5 px-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-faint"><Sparkles className="size-3 text-accent" />People you email</div>
      {suggestions.map((s) => (
        <div key={s.address} className="flex items-center gap-2.5 rounded-lg px-1 py-1">
          <Avatar name={s.name} address={s.address} size={26} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[12.5px] font-medium">{s.name}</span>
            <span className="block truncate text-[11px] text-muted">{s.address}</span>
          </span>
          <IconButton size="sm" label="Add contact" onClick={async () => {
            await api.createContact({ name: s.name, emails: [s.address], phones: [], tags: [], favorite: false, source: 'suggested' });
            qc.invalidateQueries({ queryKey: qk.contacts });
            toast.success(`Added ${s.name}`);
          }}><UserPlus /></IconButton>
          <button className="text-[11px] text-faint hover:text-muted" onClick={() => setDismissed((d) => [...d, s.address])}>✕</button>
        </div>
      ))}
    </div>
  );
}
